// src/pages/finance-portal/procurement-pos/ProcurementPOForm.js
//
// Create/Edit a Procurement PO (full-page).

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, IconButton, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    MenuItem, Select, FormControl, InputLabel, Alert, CircularProgress,
    Autocomplete, Skeleton, InputAdornment, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_TERMS = [
    { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
    { value: 'NET_15', label: 'Net 15' },
    { value: 'NET_30', label: 'Net 30' },
    { value: 'NET_60', label: 'Net 60' },
    { value: 'NET_90', label: 'Net 90' },
];

function emptyLine() {
    return { description: '', qty_ordered: 1, unit_of_measure: '',
             unit_price: '', gl_account_id: '', line_notes: '' };
}

export default function ProcurementPOForm() {
    const navigate = useNavigate();
    const { poId } = useParams();
    const isEdit = !!poId;
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState({
        vendor_id: '',
        order_date: new Date().toISOString().slice(0, 10),
        expected_delivery_date: '',
        payment_terms: 'NET_30',
        currency: 'USD',
        tax_amount: 0,
        shipping_amount: 0,
        notes: '',
    });
    const [lines, setLines] = useState([emptyLine()]);
    const [vendors, setVendors] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchVendors = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/vendors',
                { params: { per_page: 200, status: 'ACTIVE' } });
            if (res.data.success) setVendors(res.data.data || []);
        } catch { /* silent */ }
    }, []);

    const fetchGl = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) setGlAccounts(res.data.data || []);
        } catch { /* silent */ }
    }, []);

    const fetchExisting = useCallback(async () => {
        if (!isEdit) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/procurement-pos/${poId}`);
            if (res.data.success) {
                const p = res.data.data;
                setData({
                    vendor_id: p.vendor_id || '',
                    order_date: p.order_date || new Date().toISOString().slice(0, 10),
                    expected_delivery_date: p.expected_delivery_date || '',
                    payment_terms: p.payment_terms || 'NET_30',
                    currency: p.currency || 'USD',
                    tax_amount: p.tax_amount || 0,
                    shipping_amount: p.shipping_amount || 0,
                    notes: p.notes || '',
                });
                setLines((p.lines || []).map(l => ({
                    description: l.description || '',
                    qty_ordered: String(l.qty_ordered ?? 1),
                    unit_of_measure: l.unit_of_measure || '',
                    unit_price: String(l.unit_price ?? ''),
                    gl_account_id: l.gl_account_id || '',
                    line_notes: l.line_notes || '',
                })));
                if (p.status !== 'DRAFT') {
                    setError(`This PO is in ${p.status} status and cannot be edited.`);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load PO');
        } finally {
            setLoading(false);
        }
    }, [isEdit, poId]);

    useEffect(() => { fetchVendors(); fetchGl(); fetchExisting(); },
              [fetchVendors, fetchGl, fetchExisting]);

    const update = (k, v) => setData(prev => ({ ...prev, [k]: v }));
    const updateLine = (i, k, v) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
    const addLine = () => setLines(prev => [...prev, emptyLine()]);
    const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

    const subtotal = lines.reduce((s, l) =>
        s + (parseFloat(l.qty_ordered) || 0) * (parseFloat(l.unit_price) || 0), 0);
    const total = subtotal + (parseFloat(data.tax_amount) || 0) + (parseFloat(data.shipping_amount) || 0);

    const validateLocal = () => {
        const e = {};
        if (!data.vendor_id) e.vendor_id = 'Vendor is required';
        if (lines.length === 0) e.lines = 'At least one line is required';
        lines.forEach((l, i) => {
            if (!l.description?.trim()) e[`lines[${i}].description`] = 'Required';
            const q = parseFloat(l.qty_ordered);
            if (isNaN(q) || q <= 0) e[`lines[${i}].qty_ordered`] = 'Must be > 0';
            const p = parseFloat(l.unit_price);
            if (isNaN(p) || p < 0) e[`lines[${i}].unit_price`] = 'Must be ≥ 0';
        });
        return e;
    };

    const handleSave = async () => {
        const localErrors = validateLocal();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            enqueueSnackbar('Fix the highlighted errors', { variant: 'warning' });
            return;
        }
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                vendor_id: parseInt(data.vendor_id, 10),
                order_date: data.order_date,
                expected_delivery_date: data.expected_delivery_date || null,
                payment_terms: data.payment_terms,
                currency: data.currency || 'USD',
                tax_amount: parseFloat(data.tax_amount) || 0,
                shipping_amount: parseFloat(data.shipping_amount) || 0,
                notes: data.notes || null,
                lines: lines.map(l => ({
                    description: l.description.trim(),
                    qty_ordered: parseFloat(l.qty_ordered),
                    unit_of_measure: l.unit_of_measure || null,
                    unit_price: parseFloat(l.unit_price),
                    gl_account_id: l.gl_account_id || null,
                    line_notes: l.line_notes || null,
                })),
            };
            let id = poId;
            if (isEdit) {
                await axiosInstance.put(`/api/finance/procurement-pos/${poId}`, payload);
                enqueueSnackbar('PO updated', { variant: 'success' });
            } else {
                const res = await axiosInstance.post('/api/finance/procurement-pos', payload);
                id = res.data?.data?.id;
                enqueueSnackbar('PO created', { variant: 'success' });
            }
            navigate(`/finance-portal/procurement-pos/${id}`);
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar('Validation failed', { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed to save', { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };
    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/procurement-pos')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            Procurement PO
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {isEdit ? 'Edit PO' : 'New PO'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Header
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                              gap: 1.5 }}>
                        <Autocomplete size="small"
                                      options={vendors}
                                      getOptionLabel={v => v ? (v.display_name || v.company_name || `Vendor #${v.id}`) : ''}
                                      value={vendors.find(v => v.id === parseInt(data.vendor_id, 10)) || null}
                                      onChange={(_, val) => update('vendor_id', val?.id || '')}
                                      renderInput={p => <TextField {...p} label="Vendor *"
                                                                   error={!!errors.vendor_id}
                                                                   helperText={errors.vendor_id || ' '}
                                                                   InputLabelProps={{ ...p.InputLabelProps, sx: labelSx }}
                                                                   InputProps={{ ...p.InputProps, sx: fieldSx }} />} />
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Payment Terms</InputLabel>
                            <Select value={data.payment_terms} label="Payment Terms"
                                    onChange={e => update('payment_terms', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {PAYMENT_TERMS.map(t =>
                                    <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Order Date" type="date" size="small"
                                   value={data.order_date}
                                   onChange={e => update('order_date', e.target.value)}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx }} />
                        <TextField label="Expected Delivery" type="date" size="small"
                                   value={data.expected_delivery_date}
                                   onChange={e => update('expected_delivery_date', e.target.value)}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx }} />
                        <TextField label="Currency" size="small"
                                   value={data.currency}
                                   onChange={e => update('currency', e.target.value.toUpperCase())}
                                   inputProps={{ maxLength: 3 }}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    </Box>
                    <TextField label="Notes" size="small" fullWidth multiline rows={2} sx={{ mt: 1.5 }}
                               value={data.notes}
                               onChange={e => update('notes', e.target.value)}
                               InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>Lines</Typography>
                        <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={addLine}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                            Add Line
                        </Button>
                    </Box>
                    {errors.lines && <Box sx={{ p: 2 }}>
                        <Alert severity="warning" sx={{ borderRadius: '10px', fontFamily }}>{errors.lines}</Alert>
                    </Box>}
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={labelCell}>Description</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 100 }}>Qty</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 100 }}>UoM</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 150 }}>Unit Price</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 280 }}>GL Account</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 56 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lines.map((l, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            <TextField size="small" fullWidth value={l.description}
                                                       onChange={e => updateLine(i, 'description', e.target.value)}
                                                       error={!!errors[`lines[${i}].description`]}
                                                       helperText={errors[`lines[${i}].description`] || ''}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" type="number" fullWidth value={l.qty_ordered}
                                                       onChange={e => updateLine(i, 'qty_ordered', e.target.value)}
                                                       error={!!errors[`lines[${i}].qty_ordered`]}
                                                       helperText={errors[`lines[${i}].qty_ordered`] || ''}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" fullWidth value={l.unit_of_measure}
                                                       onChange={e => updateLine(i, 'unit_of_measure', e.target.value)}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" type="number" fullWidth value={l.unit_price}
                                                       onChange={e => updateLine(i, 'unit_price', e.target.value)}
                                                       error={!!errors[`lines[${i}].unit_price`]}
                                                       helperText={errors[`lines[${i}].unit_price`] || ''}
                                                       InputProps={{
                                                           startAdornment: <InputAdornment position="start"
                                                               sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.82rem' } }}>
                                                               $
                                                           </InputAdornment>,
                                                           sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <Autocomplete size="small"
                                                          options={glAccounts}
                                                          getOptionLabel={a => a ? `${a.account_number} — ${a.name}` : ''}
                                                          value={glAccounts.find(a => a.id === parseInt(l.gl_account_id, 10)) || null}
                                                          onChange={(_, val) => updateLine(i, 'gl_account_id', val?.id || '')}
                                                          renderInput={p => <TextField {...p} placeholder="Optional"
                                                                                       InputProps={{ ...p.InputProps,
                                                                                                     sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />} />
                                        </TableCell>
                                        <TableCell>
                                            {lines.length > 1 && (
                                                <IconButton size="small" onClick={() => removeLine(i)}
                                                            sx={{ color: '#ef4444' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Divider />
                    <Box sx={{ p: 2, display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                              gap: 1.5 }}>
                        <TextField label="Tax Amount" size="small" type="number"
                                   value={data.tax_amount}
                                   onChange={e => update('tax_amount', e.target.value)}
                                   InputProps={{ startAdornment: <InputAdornment position="start"
                                                                                  sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                                 sx: fieldSx }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Shipping" size="small" type="number"
                                   value={data.shipping_amount}
                                   onChange={e => update('shipping_amount', e.target.value)}
                                   InputProps={{ startAdornment: <InputAdornment position="start"
                                                                                  sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                                 sx: fieldSx }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#94a3b8' }}>
                                Subtotal: <strong>{new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency || 'USD' }).format(subtotal)}</strong>
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.1rem', color: '#0f172a' }}>
                                Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency || 'USD' }).format(total)}
                            </Typography>
                        </Box>
                    </Box>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button onClick={() => navigate('/finance-portal/procurement-pos')}
                            sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving || !!error}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                  borderRadius: '10px', bgcolor: ACCENT,
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create PO'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
